/**
 * Client-side redirect middleware that mimics the behavior of /_redirects
 * This handles redirects that would normally be handled by the server
 */
import { Platform } from 'quasar'
import { $logger } from '@/boot/logger'
import { $flagsmith } from '@/boot/flagsmith'
import { $async } from '@/boot/async'
import { startCase } from 'lodash-es'

const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV || process.env.NODE_ENV === 'development'

let redirectRules = []
let partnerExpForce = $flagsmith.hasFeature('partner_exp_force')

// Define redirect rules based on /_redirects file
function buildRedirectRules() {
  let rules = [
    // Shop redirects (specific paths must come first to override general /shop/* rule)
    {
      from: /^\/(app\/)?shop\/cart\/?$/,
      to: '$1cart',
      status: 301,
      priority: 1,
    },
    {
      from: /^\/(app\/)?shop\/checkout\/?$/,
      to: '$1checkout',
      status: 301,
      priority: 1,
    },
    {
      from: /^\/(app\/)?(shop\/)?orders\/(.*)$/,
      to: '$1order/$3',
      status: 301,
      priority: 1,
    },
    {
      from: /^\/(app\/)?shop\/product\/(.*)$/,
      to: '$1product/$2',
      status: 301,
      priority: 2,
    },
    {
      from: /^\/(app\/)?shop\/search\/?(.*)$/,
      to: '$1search/$2',
      status: 301,
      priority: 2,
    },

    // Rush Market partner redirects
    {
      from: /^\/(app\/)?rushmarket\/?(.*)$/,
      to: '$1p/rush-market/$2',
      status: 301,
      priority: 3,
    },
    {
      from: /^\/(app\/)?b\/rushmarket\/?(.*)$/,
      to: '$1p/rush-market/$2',
      status: 301,
      priority: 3,
    },
    {
      from: /^\/(app\/)?b\/rush-market\/?(.*)$/,
      to: '$1p/rush-market/$2',
      status: 301,
      priority: 3,
    },
  ]

  if ($flagsmith.hasFeature('partner_exp_force')) {
    $logger.debug('[Redirect Middleware] Applying Partner EXP redirects')
    // Partner-Specific Redirects (Temporary 302)
    rules = rules.concat([
      {
        from: /^\/(app\/)?$/,
        to: '$1p/rush-market',
        status: 302,
        priority: 1,
      },
      {
        from: /^\/(app\/)?shop\/?$/,
        to: '$1p/rush-market',
        status: 302,
        priority: 1,
      },
      {
        from: /^\/(app\/)?(shop\/)?product\/(.*)$/,
        to: '$1p/rush-market/product/$3',
        status: 302,
        priority: 1,
      },
      {
        from: /^\/(app\/)?(shop\/)?search\/?(.*)$/,
        to: '$1p/rush-market/search/$3',
        status: 302,
        priority: 1,
      },
    ])
  }

  return rules
}

/**
 * Check if a path matches any redirect rule and return the redirect target
 * @param {string} path - The current path to check
 * @returns {object|null} - Redirect object with 'to' and 'status' properties, or null if no match
 */
async function checkRedirect(path) {
  // Skip redirects for test routes in DEV environment
  if (isDev && path.startsWith('/tests')) {
    $logger.debug('[Redirect Middleware] Skipping redirects for /tests/* routes in DEV environment')
    return null
  }

  if ($flagsmith.isLoading) {
    $logger.debug('[Redirect Middleware] Flagsmith is still loading, delaying redirect check')
    await $async.delay(100)
    // Flagsmith is still loading, re-check at interval
    return checkRedirect(path)
  }
  if (!redirectRules.length || partnerExpForce !== $flagsmith.hasFeature('partner_exp_force')) {
    const rules = buildRedirectRules()
    // Sort rules by priority to ensure correct order
    redirectRules = [...rules].sort((a, b) => a.priority - b.priority)
    partnerExpForce = $flagsmith.hasFeature('partner_exp_force')
  }

  for (const rule of redirectRules) {
    const match = path.match(rule.from)
    if (match) {
      // prefix the redirect with base slash not captured in regex
      let redirectTo = `/${rule.to}`.replace(/\/\/+/g, '/')

      // ensure /app prefix for native mobile
      if (Platform.is.nativeMobile && !redirectTo.startsWith('/app')) {
        redirectTo = `/app${redirectTo}`
      }

      // Handle parameter replacement (e.g., $1, $2, etc.)
      if (match.length > 1) {
        for (let i = 1; i < match.length; i++) {
          redirectTo = redirectTo.replace(`$${i}`, match[i] ? match[i] : '')
        }
      }

      // Log which rule matched for debugging
      $logger.debug(`[Redirect Middleware] Rule matched: ${rule.from} → ${redirectTo} (priority: ${rule.priority})`)

      return {
        path: redirectTo,
        status: rule.status,
        originalPath: path,
      }
    }
  }

  return null
}

/**
 * Apply redirect middleware to a route
 * @param {object} to - The route being navigated to
 * @param {object} _from - The route being navigated from
 */
export async function applyRedirectMiddleware(to, _from) {
  const redirect = await checkRedirect(to.path)

  if (redirect) {
    // Log the redirect for debugging
    $logger.debug(`[Redirect Middleware] ${redirect.status}: ${redirect.originalPath} → ${redirect.path}`)

    const { query, hash } = to

    // Perform the redirect
    // For client-side routing, we'll use router.replace to avoid adding to history
    // for 301 redirects, and router.push for 302 redirects
    return { path: redirect.path, query, hash, replace: redirect.status === 301 }
  } else if (to.path.startsWith('/account') && to.hash) {
    // handle legacy hash-based paths for account routes
    const hashTarget = to.hash.replace('#', '')
    const validSections = ['profile', 'orders', 'watchlist', 'wallet', 'returns', 'business']
    if (validSections.includes(hashTarget)) {
      $logger.debug(`[Redirect Middleware] Navigating to account section from hash: ${to.hash}`)
      return { name: `Account${hashTarget === 'profile' ? 'Home' : startCase(hashTarget)}`, query: to.query, replace: true }
    }
  }
  // No redirect matched, continue normally
}
